import React, { useState } from 'react';
import { Card, Table, Button, Badge, Form } from 'react-bootstrap';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { NavLink, useHistory } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import { CME_USER_API } from '../../../../utils/apiUrl';
import { DeleteSingle, Get, Post } from '../../../../utils/httpProvider';
import { messageToast, status200, objectShowNumber, objectTrangThaiCME, trangThaiCMEValue } from '../../../../utils/define';
import { formatDateTimeWithoutHour } from '../../../../utils/helper';
import { URI_INTERNAL } from '../../../../utils/pathLocations';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import CMEHoSoDetailModal from './CMEHoSoDetailModal';

const CMEHoSo = () => {
    const history = useHistory();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');

    // Chi tiết modal
    const [openDetailModal, setOpenDetailModal] = useState(false);
    const [detailData, setDetailData] = useState(null);

    const { isLoading, isError, data, refetch } = useQuery(
        ['myCMEData', page, pageSize],
        async () => {
            const model = {
                PageIndex: page,
                PageSize: pageSize
            };
            const res = await Post(CME_USER_API.GET_MY_HO_SO, null, model);
            return await res.json();
        },
        { 
            keepPreviousData: true,
            onError: (err) => {
                toast.error("Lỗi khi tải dữ liệu: " + (err?.message || "Vui lòng thử lại sau"));
            }
        }
    );

    const deleteFunc = useMutation((id) => DeleteSingle(`${CME_USER_API.DELETE_HO_SO}/${id}`));

    const deleteAction = (id) => {
        setIdWantDelete(id);
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        deleteFunc.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res.status === status200 || res === true) {
                    toast.success(messageToast.messageToastDeleteSuccess);
                    refetch();
                } else {
                    toast.error(res?.message || messageToast.messageToastDeleteError);
                }
            },
            onError: (err) => {
                toast.error(err?.message || messageToast.messageToastTryCatch);
            }
        });
        setOpenConfirmDeleteModal(false);
    };

    const handleViewDetail = (item) => {
        setDetailData(item);
        setOpenDetailModal(true);
    };

    const items = data?.Data?.$values || data?.Data || data?.data?.$values || data?.data || [];
    const totalRecords = data?.TotalRecords ?? data?.totalRecords ?? 0;

    const getStatusLabel = (status) => {
        const found = objectTrangThaiCME.find((x) => x.value === status);
        return found ? (
            <Badge variant={found.color}>{found.label}</Badge>
        ) : (
            <Badge variant="secondary">Không xác định</Badge>
        );
    };

    return (
        <div className="container-fluid">
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center bg-white">
                    <h5 className="mb-0">Lịch sử tích lũy CME cá nhân</h5>
                    <NavLink to={`${URI_INTERNAL.CMECA_NHAN_PATH}/create`} className="btn btn-success">
                        <i className="fas fa-plus mr-1"></i>Nộp hồ sơ mới
                    </NavLink>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead className="thead-light">
                                <tr>
                                    <th>STT</th>
                                    <th>Nội dung học/Hoạt động</th>
                                    <th>Danh mục/Tiểu mục</th>
                                    <th>Vai trò</th>
                                    <th>Minh chứng</th>
                                    <th>Số lượng</th>
                                    <th>Số giờ</th>
                                    <th>Thời gian</th>
                                    <th>Trạng thái</th>
                                    <th>Hành động</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={10} className="text-center">Đang tải...</td></tr>
                                ) : isError ? (
                                    <tr>
                                        <td colSpan={10} className="text-center">
                                            <div className="text-danger mb-2">Đã xảy ra lỗi khi tải dữ liệu.</div>
                                            <Button variant="outline-primary" size="sm" onClick={() => refetch()}>
                                                <i className="fas fa-sync-alt mr-1"></i>Thử lại
                                            </Button>
                                        </td>
                                    </tr>
                                ) : items.length === 0 ? (
                                    <tr><td colSpan={10} className="text-center">Bạn chưa có hồ sơ nào</td></tr>
                                ) : (
                                    items.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="text-center">{(page - 1) * pageSize + index + 1}</td>
                                            <td>
                                                <div
                                                    className="font-weight-bold text-dark cursor-pointer"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleViewDetail(item)}
                                                >
                                                    {item.noiDung}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <small className="text-muted text-uppercase font-weight-bold mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                                                        {item.tenDanhMuc}
                                                    </small>
                                                    <div className="text-dark" style={{ lineHeight: '1.4', fontSize: '0.85rem', fontWeight: '500' }}>
                                                        {item.tenTieuMuc}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="text-info">{item.vaiTro || <small className="text-muted">N/A</small>}</span>
                                            </td>
                                            <td className="text-center">
                                                {(item.dinhKemFiles?.$values || item.dinhKemFiles || item.DinhKemFiles?.$values || item.DinhKemFiles || []).length > 0 ? (
                                                    <Badge variant="info" pill style={{ cursor: 'pointer' }} onClick={() => handleViewDetail(item)}>
                                                        <i className="fas fa-paperclip mr-1"></i>
                                                        {(item.dinhKemFiles?.$values || item.dinhKemFiles || item.DinhKemFiles?.$values || item.DinhKemFiles || []).length} file
                                                    </Badge>
                                                ) : (
                                                    <small className="text-muted">Không có</small>
                                                )}
                                            </td>
                                            <td className="text-center">{item.soLuong}</td>
                                            <td className="text-center font-weight-bold text-primary">{item.soGioTinChi}h</td>
                                            <td>
                                                <div className="small">
                                                    <div><i className="far fa-calendar-alt mr-1"></i>{formatDateTimeWithoutHour(item.tuNgay)}</div>
                                                    <div><i className="far fa-calendar-check mr-1"></i>{formatDateTimeWithoutHour(item.denNgay)}</div>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                {getStatusLabel(item.trangThai)}
                                                {item.trangThai === trangThaiCMEValue.tuChoi && item.lyDoTuChoi && (
                                                    <div className="text-danger small mt-1" title={item.lyDoTuChoi}>
                                                        <i className="fas fa-info-circle mr-1"></i> {item.lyDoTuChoi}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center">
                                                    <Button
                                                        variant="outline-info"
                                                        size="sm"
                                                        className="mr-1"
                                                        title="Xem chi tiết"
                                                        onClick={() => handleViewDetail(item)}
                                                    >
                                                        Chi tiết
                                                    </Button>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="mr-1"
                                                        disabled={item.trangThai !== trangThaiCMEValue.choDuyet && item.trangThai !== trangThaiCMEValue.tuChoi}
                                                        title="Chỉnh sửa"
                                                        onClick={() => history.push(`${URI_INTERNAL.CMECA_NHAN_PATH}/edit/${item.id}`)}
                                                    >
                                                        Sửa
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        disabled={item.trangThai !== trangThaiCMEValue.choDuyet}
                                                        title="Xóa bỏ"
                                                        onClick={() => deleteAction(item.id)}
                                                    >
                                                       Xóa
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Phân trang */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Form.Control as="select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ width: '80px' }}>
                            {objectShowNumber.map(opt => <option key={opt.value} value={opt.value}>{opt.text}</option>)}
                        </Form.Control>
                        <Pagination
                            activePage={page}
                            itemsCountPerPage={pageSize}
                            totalItemsCount={totalRecords}
                            pageRangeDisplayed={5}
                            onChange={(num) => setPage(num)}
                            itemClass="page-item"
                            linkClass="page-link"
                        />
                    </div>
                </Card.Body>
            </Card>

            <ConfirmModal
                isOpen={openConfirmDeleteModal}
                confirmType="delete"
                handleAccept={acceptDelete}
                handleCancel={() => setOpenConfirmDeleteModal(false)}
                content="Bạn có chắc chắn muốn xóa hồ sơ này? Hành động này không thể hoàn tác."
            />

            <CMEHoSoDetailModal
                isOpen={openDetailModal}
                handleClose={() => setOpenDetailModal(false)}
                data={detailData}
            />
        </div>
    );
};

export default CMEHoSo;
